import * as React from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Divider,
  TextField,
} from "@material-ui/core";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import EditIcon from "@material-ui/icons/Edit";
import { IMedicationDetails } from "../types/PrescriptionIngestionTypes";
import { IFieldErrors } from "../utils/ValidationUtils";
import { MEDICATIONS } from "../config/PrescriptionDefaults";

interface IMedicationSelectorProps {
  selectedMedication: string;
  medicationDetails: IMedicationDetails;
  onMedicationChange: (key: string) => void;
  onMedicationFieldChange?: (field: keyof IMedicationDetails, value: any) => void;
  fieldErrors?: IFieldErrors;
  // Opt-in: render Refills as an editable field (default read-only, as other workflows expect).
  editableRefills?: boolean;
}

const MedicationSelector: React.FC<IMedicationSelectorProps> = ({
  selectedMedication,
  medicationDetails,
  onMedicationChange,
  onMedicationFieldChange,
  fieldErrors = {},
  editableRefills = false,
}) => {
  const handleFieldChange = (field: keyof IMedicationDetails, value: any) => {
    if (onMedicationFieldChange) {
      onMedicationFieldChange(field, value);
    }
  };

  return (
    <Grid item xs={12}>
      <Paper
        style={{ padding: 24, backgroundColor: "#f5f5f5", borderRadius: 8 }}
      >
        <Typography
          variant="h6"
          gutterBottom
          style={{
            color: "black",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <LocalPharmacyIcon style={{ color: "#546e7a" }} />
          Medication Details
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 16 }}
        >
          Source of Truth - Select from Dropdown
        </Typography>
        <Divider style={{ marginBottom: 20 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel>Select Medication</InputLabel>
              <Select
                value={selectedMedication}
                onChange={(e) => onMedicationChange(e.target.value as string)}
                label="Select Medication"
              >
                {Object.entries(MEDICATIONS).map(([key, medication]) => {
                  // Use current medication details if this is the selected medication
                  const displayDaysSupply = key === selectedMedication 
                    ? medicationDetails.daysSupply 
                    : medication.daysSupply;
                  
                  return (
                    <MenuItem key={key} value={key}>
                      {medication.drugDescription} - {displayDaysSupply} days supply
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Paper
              style={{
                padding: 16,
                backgroundColor: "#ffffff",
                borderLeft: "4px solid #546e7a",
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{
                  color: "black",
                  fontWeight: 600,
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <LocalPharmacyIcon style={{ color: "#546e7a", fontSize: 20 }} />
                Selected Medication Details
                <EditIcon style={{ color: "#ff9800", fontSize: 16 }} />
                <Typography variant="caption" style={{ color: "#ff9800", fontStyle: "italic" }}>
                  (Editable fields marked with *)
                </Typography>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Drug
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {medicationDetails.drugDescription}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Strength
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {medicationDetails.strength.value}{" "}
                    {medicationDetails.strength.unitOfMeasureDescription}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Quantity *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={medicationDetails.quantity.value}
                    onChange={(e) => {
                      const newQuantity = {
                        ...medicationDetails.quantity,
                        value: e.target.value,
                      };
                      handleFieldChange('quantity', newQuantity);
                    }}
                    type="number"
                    inputProps={{ min: 1, max: 9999 }}
                    helperText={fieldErrors?.quantity || `1-9999 ${medicationDetails.quantity.unitOfMeasureDescription}`}
                    error={!!fieldErrors?.quantity}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Days Supply *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={medicationDetails.daysSupply}
                    onChange={(e) => handleFieldChange('daysSupply', e.target.value)}
                    type="number"
                    inputProps={{ min: 1, max: 365 }}
                    helperText={fieldErrors?.daysSupply || "1-365 days"}
                    error={!!fieldErrors?.daysSupply}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {editableRefills ? (
                    <TextField
                      id="medication-refills"
                      label="Refills"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={medicationDetails.numberOfRefills}
                      onChange={(e) => handleFieldChange('numberOfRefills', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, max: 99 }}
                      helperText={fieldErrors?.numberOfRefills || "0-99 refills"}
                      error={!!fieldErrors?.numberOfRefills}
                    />
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Refills
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {medicationDetails.numberOfRefills}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    label="NDC *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={medicationDetails.ndc}
                    onChange={(e) => handleFieldChange('ndc', e.target.value)}
                    helperText={fieldErrors?.ndc || "National Drug Code"}
                    inputProps={{ maxLength: 11 }}
                    error={!!fieldErrors?.ndc}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Instructions (Sig Text) *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={medicationDetails.sigText}
                    onChange={(e) => handleFieldChange('sigText', e.target.value)}
                    helperText={fieldErrors?.sigText || "Patient instructions for taking the medication"}
                    inputProps={{ maxLength: 500 }}
                    error={!!fieldErrors?.sigText}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="medication-writtenDate"
                    label="Written Date *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="date"
                    value={medicationDetails.writtenDate}
                    onChange={(e) => handleFieldChange('writtenDate', e.target.value)}
                    helperText={fieldErrors?.writtenDate || "Date the prescription was written"}
                    error={!!fieldErrors?.writtenDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Pharmacy Note"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={medicationDetails.note || ""}
                    onChange={(e) => handleFieldChange('note', e.target.value || undefined)}
                    helperText="e.g., Clinical Trial Rx - iCare"
                    inputProps={{ maxLength: 200 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default MedicationSelector;
